// src/admin/pages/SubscriptionManagement.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../config/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { showToast, CustomToastContainer } from '../../admin/components/CustomToast';
import '../../styles/TenantManage.css';

const SubscriptionManagement = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const tenantDoc = await getDoc(doc(db, 'tenants', tenantId));
        if (tenantDoc.exists()) {
          setTenant({ id: tenantDoc.id, ...tenantDoc.data() });
        } else {
          showToast('Tenant not found', 'error');
          navigate('/admin/tenants');
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching tenant:', error);
        showToast('Failed to load tenant data', 'error');
        setLoading(false);
        navigate('/admin/tenants');
      }
    };
    fetchTenant();
  }, [tenantId, navigate]);

  const handlePlanChange = async (newPlan) => {
    if (!window.confirm(`Are you sure you want to change the subscription plan to ${newPlan}?`)) {
      return;
    }

    try {
      setUpdating(true);
      const tenantRef = doc(db, 'tenants', tenantId);
      await updateDoc(tenantRef, {
        subscriptionPlan: newPlan,
        globalAdminLimit: getAdminLimit(newPlan, 'global'),
        locationAdminLimit: getAdminLimit(newPlan, 'location')
      });
      showToast('Subscription plan updated successfully');
      // Refresh tenant data
      const tenantDoc = await getDoc(tenantRef);
      setTenant({ id: tenantDoc.id, ...tenantDoc.data() });
    } catch (error) {
      console.error('Error updating subscription:', error);
      showToast('Failed to update subscription', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!window.confirm(`Are you sure you want to change the status to ${newStatus}?`)) {
      return;
    }

    try {
      setUpdating(true);
      const tenantRef = doc(db, 'tenants', tenantId);
      await updateDoc(tenantRef, {
        status: newStatus
      });
      showToast('Status updated successfully');
      // Refresh tenant data
      const tenantDoc = await getDoc(tenantRef);
      setTenant({ id: tenantDoc.id, ...tenantDoc.data() });
    } catch (error) {
      console.error('Error updating status:', error);
      showToast('Failed to update status', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const getAdminLimit = (plan, adminType) => {
    if (plan === 'free') {
      return adminType === 'global' ? 1 : 1;
    } else if (plan === 'basic') {
      return adminType === 'global' ? 3 : 5;
    } else if (plan === 'premium') {
      return 'unlimited';
    }
    return 0;
  };

  if (loading) return <div>Loading tenant data...</div>;
  if (!tenant) return <div>Tenant not found</div>;

  return (
    <div className="subscription-management">
      <CustomToastContainer />
        <div className="page-header">
            <backButton 
                className="backbutton"
                onClick={() => navigate('/admin/tenants')}
                >
               &larr; Back to Tenants
            </backButton>
            <h1 className="header-center"> Subscription Management </h1>
        </div>

      <div className="subscription-info">
        <h2>Current Plan</h2>
        <div className="plan-details">
          <p><strong>Tenant Name:</strong> {tenant.name}</p>
          <p><strong>Plan:</strong> {tenant.subscriptionPlan}</p>
          <p><strong>Global Admins:</strong> 
            {tenant.globalAdminLimit === 'unlimited' 
              ? ' Unlimited' 
              : ` ${tenant.globalAdminCount || 0}/${tenant.globalAdminLimit}`}
          </p>
          <p><strong>Location Admins:</strong> 
            {tenant.locationAdminLimit === 'unlimited' 
              ? ' Unlimited' 
              : ` ${tenant.locationAdminCount || 0}/${tenant.locationAdminLimit}`}
          </p>
        </div>

        <h2>Change Plan</h2>
        <div className="plan-options">
          <div className={`plan-option ${tenant.subscriptionPlan === 'free' ? 'active' : ''}`}>
            <h3>Free</h3>
            <ul>
              <li>1 Global Admin</li>
              <li>1 Location Admin</li>
              <li>Basic Features</li>
            </ul>
            <button 
              onClick={() => handlePlanChange('free')}
              disabled={tenant.subscriptionPlan === 'free' || updating}
            >
              {tenant.subscriptionPlan === 'free' ? 'Current Plan' : 'Select Free'}
            </button>
          </div>

          <div className={`plan-option ${tenant.subscriptionPlan === 'basic' ? 'active' : ''}`}>
            <h3>Basic</h3>
            <ul>
              <li>3 Global Admins</li>
              <li>5 Location Admins</li>
              <li>Advanced Features</li>
            </ul>
            <button 
              onClick={() => handlePlanChange('basic')}
              disabled={tenant.subscriptionPlan === 'basic' || updating}
            >
              {tenant.subscriptionPlan === 'basic' ? 'Current Plan' : 'Select Basic'}
            </button>
          </div>

          <div className={`plan-option ${tenant.subscriptionPlan === 'premium' ? 'active' : ''}`}>
            <h3>Premium</h3>
            <ul>
              <li>Unlimited Global Admins</li>
              <li>Unlimited Location Admins</li>
              <li>All Features</li>
            </ul>
            <button 
              onClick={() => handlePlanChange('premium')}
              disabled={tenant.subscriptionPlan === 'premium' || updating}
            >
              {tenant.subscriptionPlan === 'premium' ? 'Current Plan' : 'Select Premium'}
            </button>
          </div>
        </div>

        <h2>Account Status</h2>
        <div className="status-options">
          <select
            value={tenant.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={updating}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManagement;